/**
 * Validadores de avance entre pasos del wizard.
 *
 * Antes vivían como condiciones largas dentro del JSX del botón "Siguiente".
 * Aquí se vuelven funciones puras, fáciles de testear y de razonar.
 */

import { STAY_TYPE_NIGHT } from './constants';

/**
 * El usuario puede avanzar del paso 0 (habitación) si seleccionó tipo,
 * número, fecha de entrada y, si es estancia por noches, también la salida.
 *
 * Nota: stayType puede ser el string legacy 'night' o el _id de un stay type
 * dinámico. Para saber si es "por noches" hay que resolverlo contra la lista
 * de stayTypes; comparar solo contra 'night' dejaba pasar reservas sin fecha
 * de salida cuando el stayType era un _id (y Yup las bloqueaba silenciosamente
 * en el paso de pago).
 *
 * @param {object} values - Formik values
 * @param {Array} [stayTypes] - stay types del hotel (para resolver isNightly)
 * @returns {boolean}
 */
export const canAdvanceFromRoomStep = (values, stayTypes = []) => {
  if (!values.roomType || !values.roomNo || !values.checkInDate) return false;
  const selected = stayTypes.find((type) => type._id === values.stayType);
  const isNightly = selected ? selected.isNightly : values.stayType === STAY_TYPE_NIGHT || !values.stayType;
  if (isNightly && !values.checkOutDate) return false;
  return true;
};

/**
 * El usuario puede avanzar del paso 1 (huésped) si el huésped principal
 * tiene nombre, apellido, tipo y número de identificación, y dirección.
 *
 * @param {object} values - Formik values
 * @returns {boolean}
 */
export const canAdvanceFromCustomerStep = (values) => {
  const primary = values?.customers?.[0];
  if (!primary) return false;
  return Boolean(
    primary.firstName &&
      primary.lastName &&
      primary.idCardType &&
      primary.idCardType !== 'default' &&
      primary.idcardNumber &&
      primary.address
  );
};

/** Etiquetas ES para los campos del esquema, usadas al listar errores. */
const FIELD_LABELS = {
  roomType: 'Tipo de habitación',
  roomNo: 'Número de habitación',
  checkInDate: 'Fecha de entrada',
  checkOutDate: 'Fecha de salida',
  advanceAmount: 'Anticipo',
  totalAmount: 'Total',
  paymentMethod: 'Método de pago',
  phoneNumber: 'Teléfono',
  firstName: 'Nombre',
  lastName: 'Apellido',
  email: 'Email',
  idCardType: 'Tipo de identificación',
  idcardNumber: 'Número de identificación',
  address: 'Dirección'
};

/**
 * Vuelve legibles los mensajes de cast de Yup (vienen en inglés y con
 * detalles técnicos tipo "must be a `date` type, but the final value...").
 */
const humanizeMessage = (message) => {
  if (typeof message !== 'string') return String(message);
  if (message.includes('must be a `date` type')) return 'fecha requerida o inválida';
  if (message.includes('must be a `number` type')) return 'debe ser un número';
  return message;
};

/**
 * Convierte el objeto `errors` de Formik en una lista legible en español.
 *
 * El botón de confirmar depende de la validación Yup COMPLETA, que incluye
 * campos de pasos anteriores (ej. la cédula o el email del huésped). Sin esta
 * lista, un error en un paso previo bloqueaba el botón sin ninguna pista
 * visible en el paso de pago.
 *
 * @param {object} errors - Formik errors
 * @returns {string[]} ej. ['Huésped 1 — Número de identificación: Formato inválido']
 */
export const listValidationIssues = (errors = {}) => {
  const issues = [];
  Object.entries(errors).forEach(([key, value]) => {
    if (key === 'customers') {
      if (typeof value === 'string') {
        issues.push(value);
        return;
      }
      (Array.isArray(value) ? value : []).forEach((customerErrors, i) => {
        if (!customerErrors || typeof customerErrors === 'string') return;
        Object.entries(customerErrors).forEach(([field, message]) => {
          issues.push(`Huésped ${i + 1} — ${FIELD_LABELS[field] || field}: ${humanizeMessage(message)}`);
        });
      });
      return;
    }
    if (typeof value === 'string') {
      issues.push(`${FIELD_LABELS[key] || key}: ${humanizeMessage(value)}`);
    }
  });
  return issues;
};

/**
 * Determina si la reserva puede confirmarse en el paso 3 (pago).
 *
 * @param {object} params
 * @param {object} params.values - Formik values
 * @param {boolean} params.isValid - resultado de la validación Yup de Formik
 * @param {boolean} params.formSubmitting - true si ya hay un submit en curso
 * @param {boolean} params.showSplitPayments - modo de pago activo
 * @param {(advance: string|number) => boolean} params.areSplitPaymentsValid
 * @returns {boolean}
 */
export const canSubmitReservation = ({
  values,
  isValid,
  formSubmitting,
  showSplitPayments,
  areSplitPaymentsValid
}) => {
  if (formSubmitting) return false;

  if (showSplitPayments) {
    const advance = parseFloat(values.advanceAmount || 0);
    if (!Number.isFinite(advance) || advance <= 0) return false;
    return areSplitPaymentsValid(values.advanceAmount);
  }

  return Boolean(isValid);
};
